export type TTourId = 'project' | 'challenge' | 'practice' | 'codeframe';

export interface ITourStep {
  /** Matches data-tour="<target>" attribute on a DOM element */
  target: string;
  /** Short headline for the tooltip */
  title: string;
  /** 1-2 sentence description */
  description: string;
  /** Alt description when Merlin AI is open */
  merlinDescription?: string;
  /** Preferred placement of the tooltip relative to the target */
  placement: 'top' | 'bottom' | 'left' | 'right';
  /** Optional: navigate to a specific pane tab when this step activates */
  navigateToTab?: string;
}

// Flow: workspace → tabs → footer tools → settings → vibe code → submit/save

// ─── Project Tour (13 steps) ───────────────────────────────────

const PROJECT_TOUR: ITourStep[] = [
  {
    target: 'code-editor',
    title: 'Code Editor',
    description:
      'Write HTML, CSS, and JS using the tabs. Use Cmd/Ctrl+Shift+F to format your code.',
    merlinDescription:
      'Merlin AI is writing code for you — close her panel when done to see it here. Take a quick tour while she works!',
    placement: 'right',
  },
  {
    target: 'preview-panel',
    title: 'Live Preview',
    description:
      'See your code rendered in real time as you type.',
    placement: 'left',
  },
  {
    target: 'pane-tab-browser',
    title: 'Browser',
    description:
      'Preview your code in a live browser view. Toggle the design overlay to compare.',
    placement: 'bottom',
    navigateToTab: 'browser',
  },
  {
    target: 'pane-tab-cards',
    title: 'Cards',
    description:
      'Track your progress with task cards. Mark them as you complete each requirement.',
    placement: 'bottom',
    navigateToTab: 'cards',
  },
  {
    target: 'pane-tab-discussions',
    title: 'Discussions',
    description:
      'Ask questions or share your approach with the community.',
    placement: 'bottom',
    navigateToTab: 'discussions',
  },
  {
    target: 'footer-assets',
    title: 'Assets & Design',
    description:
      'Access design screenshots, extract text, upload files, and add npm packages.',
    placement: 'top',
  },
  {
    target: 'console-button',
    title: 'Console',
    description:
      'Debug with the JS console. View logs, warnings, and errors from your code.',
    placement: 'top',
  },
  {
    target: 'shortcuts-button',
    title: 'Shortcuts',
    description:
      'Cmd/Ctrl+S to save, Cmd/Ctrl+Shift+F to format. View all keyboard shortcuts here.',
    placement: 'top',
  },
  {
    target: 'editor-settings',
    title: 'Settings',
    description:
      'Change framework, CSS mode, theme, font size, and more.',
    placement: 'bottom',
  },
  {
    target: 'layout-mode',
    title: 'Layout',
    description:
      'Switch between side-by-side, top-bottom, reversed, or preview-only layouts.',
    placement: 'bottom',
  },
  {
    target: 'merlin-ai',
    title: 'Vibe Code',
    description:
      'Your AI coding assistant. Ask questions, get help, or vibe code entire solutions.',
    placement: 'bottom',
  },
  {
    target: 'submit-button',
    title: 'Submit Solution',
    description:
      'Submit your code when ready, or link an external GitHub deployment.',
    placement: 'bottom',
  },
  {
    target: 'tour-pill',
    title: 'Replay Tour',
    description:
      'Click here anytime to replay this guided tour and revisit all features.',
    placement: 'top',
  },
];

// ─── Challenge Tour (13 steps) ─────────────────────────────────

const CHALLENGE_TOUR: ITourStep[] = [
  {
    target: 'code-editor',
    title: 'Code Editor',
    description:
      'Write HTML, CSS, and JS using the tabs. Use Cmd/Ctrl+Shift+F to format your code.',
    merlinDescription:
      'Merlin AI is writing code for you — close her panel when done to see it here. Take a quick tour while she works!',
    placement: 'right',
  },
  {
    target: 'preview-panel',
    title: 'Live Preview',
    description:
      'See your code rendered in real time as you type.',
    placement: 'left',
  },
  {
    target: 'pane-tab-browser',
    title: 'Browser',
    description:
      'Preview your code in a live browser view. Toggle the design overlay to compare.',
    placement: 'bottom',
    navigateToTab: 'browser',
  },
  {
    target: 'pane-tab-instructions',
    title: 'Instructions',
    description:
      'Read acceptance criteria and requirements for the challenge.',
    placement: 'bottom',
    navigateToTab: 'instructions',
  },
  {
    target: 'pane-tab-discussions',
    title: 'Discussions',
    description:
      'Ask questions or share your approach with the community.',
    placement: 'bottom',
    navigateToTab: 'discussions',
  },
  {
    target: 'footer-assets',
    title: 'Assets & Design',
    description:
      'Access the design screenshot, extract text, upload files, and add npm packages.',
    placement: 'top',
  },
  {
    target: 'console-button',
    title: 'Console',
    description:
      'Debug with the JS console. View logs, warnings, and errors from your code.',
    placement: 'top',
  },
  {
    target: 'shortcuts-button',
    title: 'Shortcuts',
    description:
      'Cmd/Ctrl+S to save, Cmd/Ctrl+Shift+F to format. View all keyboard shortcuts here.',
    placement: 'top',
  },
  {
    target: 'editor-settings',
    title: 'Settings',
    description:
      'Change framework, CSS mode, theme, font size, and more.',
    placement: 'bottom',
  },
  {
    target: 'layout-mode',
    title: 'Layout',
    description:
      'Switch between side-by-side, top-bottom, reversed, or preview-only layouts.',
    placement: 'bottom',
  },
  {
    target: 'merlin-ai',
    title: 'Vibe Code',
    description:
      'Your AI coding assistant. Ask questions, get help, or vibe code entire solutions.',
    placement: 'bottom',
  },
  {
    target: 'submit-button',
    title: 'Submit Solution',
    description:
      'Submit your code when ready, or link an external GitHub deployment.',
    placement: 'bottom',
  },
  {
    target: 'tour-pill',
    title: 'Replay Tour',
    description:
      'Click here anytime to replay this guided tour and revisit all features.',
    placement: 'top',
  },
];

// ─── Practice Problem Tour (10 steps) ───────────────────────────

const PRACTICE_TOUR: ITourStep[] = [
  {
    target: 'code-editor',
    title: 'Code Editor',
    description:
      'Implement the function in JavaScript. Only the JS tab is available for problems.',
    merlinDescription:
      'Merlin AI is writing code for you — close her panel when done to see it here. Take a quick tour while she works!',
    placement: 'right',
  },
  {
    target: 'pane-tab-instructions',
    title: 'Instructions',
    description:
      'Read the problem description, constraints, and examples.',
    placement: 'bottom',
    navigateToTab: 'instructions',
  },
  {
    target: 'pane-tab-discussions',
    title: 'Discussions',
    description:
      'Discuss approaches with others or ask for hints.',
    placement: 'bottom',
    navigateToTab: 'discussions',
  },
  {
    target: 'test-cases',
    title: 'Test Cases',
    description:
      'View expected inputs and outputs. Use Cmd/Ctrl+Enter to run tests instantly.',
    placement: 'top',
  },
  {
    target: 'console-button',
    title: 'Console',
    description:
      'Debug with the JS console. View logs and errors from your code.',
    placement: 'top',
  },
  {
    target: 'shortcuts-button',
    title: 'Shortcuts',
    description:
      'Cmd/Ctrl+Enter to run tests, Cmd/Ctrl+Shift+F to format. View all shortcuts here.',
    placement: 'top',
  },
  {
    target: 'editor-settings',
    title: 'Settings',
    description:
      'Adjust font size, tab size, theme, and other editor preferences.',
    placement: 'bottom',
  },
  {
    target: 'merlin-ai',
    title: 'Vibe Code',
    description:
      'Your AI coding assistant. Ask questions or get hints when you are stuck.',
    placement: 'bottom',
  },
  {
    target: 'submit-button',
    title: 'Submit',
    description:
      'All test cases must pass before you can submit your solution.',
    placement: 'bottom',
  },
  {
    target: 'tour-pill',
    title: 'Replay Tour',
    description:
      'Click here anytime to replay this guided tour and revisit all features.',
    placement: 'top',
  },
];

// ─── CodeFrame / Prototype Tour (13 steps) ─────────────────────

const CODEFRAME_TOUR: ITourStep[] = [
  {
    target: 'code-editor',
    title: 'Code Editor',
    description:
      'Write HTML, CSS, and JS using the tabs. Use Cmd/Ctrl+Shift+F to format your code.',
    merlinDescription:
      'Merlin AI is writing code for you — close her panel when done to see it here. Take a quick tour while she works!',
    placement: 'right',
  },
  {
    target: 'preview-panel',
    title: 'Live Preview',
    description:
      'See your code rendered in real time as you type.',
    placement: 'left',
  },
  {
    target: 'pane-tab-browser',
    title: 'Browser',
    description:
      'Preview your rendered code in a live browser view.',
    placement: 'bottom',
    navigateToTab: 'browser',
  },
  {
    target: 'pane-tab-comments',
    title: 'Comments',
    description:
      'Get feedback from the community on your code.',
    placement: 'bottom',
    navigateToTab: 'comments',
  },
  {
    target: 'pane-tab-explore',
    title: 'Explore',
    description:
      'Browse and learn from what others have built.',
    placement: 'bottom',
    navigateToTab: 'explore',
  },
  {
    target: 'footer-assets',
    title: 'Files & Packages',
    description:
      'Upload your own files and add npm packages to extend your code.',
    placement: 'top',
  },
  {
    target: 'console-button',
    title: 'Console',
    description:
      'Debug with the JS console. View logs, warnings, and errors from your code.',
    placement: 'top',
  },
  {
    target: 'shortcuts-button',
    title: 'Shortcuts',
    description:
      'Cmd/Ctrl+S to save, Cmd/Ctrl+Shift+F to format. View all keyboard shortcuts here.',
    placement: 'top',
  },
  {
    target: 'editor-settings',
    title: 'Settings',
    description:
      'Change framework, CSS mode, theme, font size, and more.',
    placement: 'bottom',
  },
  {
    target: 'layout-mode',
    title: 'Layout',
    description:
      'Switch between side-by-side, top-bottom, reversed, or preview-only layouts.',
    placement: 'bottom',
  },
  {
    target: 'merlin-ai',
    title: 'Vibe Code',
    description:
      'Your AI coding assistant. Ask questions, get help, or vibe code your ideas.',
    placement: 'bottom',
  },
  {
    target: 'save-button',
    title: 'Save',
    description:
      'Save your code to persist it across sessions. Use Cmd/Ctrl+S to save quickly.',
    placement: 'bottom',
  },
  {
    target: 'tour-pill',
    title: 'Replay Tour',
    description:
      'Click here anytime to replay this guided tour and revisit all features.',
    placement: 'top',
  },
];

export const TOUR_CONFIGS: Record<TTourId, ITourStep[]> = {
  project: PROJECT_TOUR,
  challenge: CHALLENGE_TOUR,
  practice: PRACTICE_TOUR,
  codeframe: CODEFRAME_TOUR,
};
